import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Eye, Clock, Users, TrendingUp, DollarSign, Plus, Upload, Film, Send, Trash2, Link2, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { COUNTRY_LIST, LANGUAGES, CONTENT_TYPES, typeLabel } from "@/lib/constants";

const STATUS_STYLES = {
  APPROVED: "bg-emerald-500/20 text-emerald-300 border-emerald-400/30",
  PENDING: "bg-amber-500/20 text-amber-300 border-amber-400/30",
  DRAFT: "bg-white/10 text-white/60 border-white/20",
  REJECTED: "bg-red-500/20 text-red-300 border-red-400/30",
  REMOVED: "bg-red-500/20 text-red-300 border-red-400/30",
};

const emptyForm = {
  title: "", description: "", thumbnail: "", category: "", country: "ZA",
  language: "English", content_type: "CREATOR_CONTENT", duration: 0, age_rating: "PG",
  subscription_required: false, pay_per_view_price: 0, video_url: "", trailer_url: "", cast: [],
  series_id: "", season: 1, episode_number: 1,
};

export default function CreatorDashboard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [config, setConfig] = useState({ mux_enabled: false, cloudinary_enabled: false });
  const [payouts, setPayouts] = useState(null);
  const [wAmount, setWAmount] = useState("");

  const loadPayouts = () => api.get("/creator/payouts").then((r) => setPayouts(r.data)).catch(() => {});
  const load = () => api.get("/creator/dashboard").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load(); loadPayouts();
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const withdraw = async () => {
    const amt = Number(wAmount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    try { await api.post("/creator/payouts", { amount: amt, method: "paypal" }); setWAmount(""); toast.success("Withdrawal requested"); loadPayouts(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <div className="min-h-[50vh] flex items-center justify-center"><div className="w-10 h-10 border-4 border-[#E50914] border-t-transparent rounded-full animate-spin" /></div>;

  const s = data.summary;

  const submitForReview = async (id) => {
    try { await api.post(`/creator/content/${id}/submit`); toast.success("Submitted for admin review"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/creator/content/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#D4AF37]">Creator Studio</p>
          <h1 className="font-display font-black text-3xl sm:text-4xl">{data.creator.display_name}</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="upload-open-btn" className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#E50914] hover:bg-[#FF2A3B] font-semibold transition-colors">
              <Plus className="w-5 h-5" /> Upload Content
            </button>
          </DialogTrigger>
          <UploadDialog categories={categories} config={config} seriesList={(data.content || []).filter((c) => c.content_type === "SERIES")} onDone={() => { setOpen(false); load(); loadPayouts(); }} />
        </Dialog>
      </div>

      {payouts && (
        <div className="glass rounded-2xl p-5 mb-8" data-testid="payouts-panel">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
            <div className="flex items-center gap-8">
              <div><p className="text-xs text-white/50 uppercase tracking-wider">Available</p><p className="font-display font-black text-2xl text-[#D4AF37]">R{payouts.available}</p></div>
              <div><p className="text-xs text-white/50 uppercase tracking-wider">Requested/Paid</p><p className="font-display font-black text-2xl">R{payouts.withdrawn}</p></div>
              <div><p className="text-xs text-white/50 uppercase tracking-wider">Lifetime</p><p className="font-display font-black text-2xl">R{payouts.earnings}</p></div>
            </div>
            <div className="flex items-end gap-2">
              <div><label className="text-[10px] uppercase text-white/40 block mb-1">Amount (R)</label>
                <input type="number" value={wAmount} onChange={(e) => setWAmount(e.target.value)} data-testid="withdraw-amount"
                  className="h-11 w-32 px-3 rounded-lg bg-white/5 border border-white/10 focus:border-[#E50914]/60 focus:outline-none" /></div>
              <button onClick={withdraw} data-testid="withdraw-btn" className="h-11 px-5 rounded-lg bg-[#E50914] hover:bg-[#FF2A3B] font-semibold transition-colors">Withdraw</button>
            </div>
          </div>
          {payouts.payouts.length > 0 && (
            <div className="mt-4 border-t border-white/10 pt-3 space-y-2" data-testid="payout-history">
              {payouts.payouts.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-sm">
                  <span className="text-white/60">R{p.amount} · {p.method} · {new Date(p.created_at).toLocaleDateString()}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === "paid" ? "bg-emerald-500/20 text-emerald-300" : p.status === "rejected" ? "bg-red-500/20 text-red-300" : "bg-amber-500/20 text-amber-300"}`}>{p.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
        <Stat icon={Film} label="Titles" value={s.total_content} />
        <Stat icon={Eye} label="Total Views" value={s.total_views.toLocaleString()} />
        <Stat icon={Clock} label="Watch Hours" value={s.total_watch_hours} />
        <Stat icon={Users} label="Unique Viewers" value={s.unique_viewers} />
        <Stat icon={TrendingUp} label="Avg Completion" value={`${s.avg_completion_rate}%`} />
        <Stat icon={DollarSign} label="Earnings" value={`R${s.earnings}`} gold />
      </div>

      <h2 className="font-display font-bold text-xl mb-4">Your Content & Analytics</h2>
      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="creator-content-table">
            <thead className="bg-white/5 text-white/50 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Views</th>
                <th className="text-right px-4 py-3">Unique</th>
                <th className="text-right px-4 py-3">Completion</th>
                <th className="text-right px-4 py-3">Earnings</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.content.map((c) => (
                <tr key={c.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`content-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img src={c.thumbnail} alt="" className="w-12 h-8 rounded object-cover" />
                      <div className="min-w-0">
                        <span className="font-medium truncate max-w-[180px] block">{c.title}</span>
                        {c.status === "REJECTED" && c.rejection_reason && (
                          <span className="text-xs text-red-300 flex items-center gap-1 mt-0.5" data-testid={`reject-reason-${c.id}`}>
                            <AlertCircle className="w-3 h-3 shrink-0" /> {c.rejection_reason}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full border text-xs ${STATUS_STYLES[c.status]}`}>{c.status}</span></td>
                  <td className="px-4 py-3 text-right">{c.views}</td>
                  <td className="px-4 py-3 text-right">{c.unique_viewers}</td>
                  <td className="px-4 py-3 text-right">{c.completion_rate}%</td>
                  <td className="px-4 py-3 text-right text-[#D4AF37]">R{c.earnings}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {c.status === "DRAFT" && (
                        <button onClick={() => submitForReview(c.id)} data-testid={`submit-${c.id}`} title="Submit for review"
                          className="p-2 rounded-lg bg-[#E50914]/20 text-[#FF6B75] hover:bg-[#E50914]/30 transition-colors"><Send className="w-4 h-4" /></button>
                      )}
                      <button onClick={() => del(c.id)} data-testid={`delete-${c.id}`} title="Delete"
                        className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 hover:text-red-300 transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!data.content.length && <tr><td colSpan={7} className="px-4 py-10 text-center text-white/40">No content yet. Upload your first title!</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function UploadDialog({ categories, config, seriesList = [], onDone }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const createAndSubmit = async () => {
    if (!form.title) return toast.error("Title is required");
    setSaving(true);
    try {
      let videoUrl = form.video_url;
      const file = fileRef.current?.files?.[0];
      if (file && config.cloudinary_enabled) {
        setUploading(true);
        try {
          const { data: sig } = await api.get("/cloudinary/signature?resource_type=video");
          const fd = new FormData();
          fd.append("file", file);
          fd.append("api_key", sig.api_key);
          fd.append("timestamp", sig.timestamp);
          fd.append("signature", sig.signature);
          fd.append("folder", sig.folder);
          const up = await new Promise((res, rej) => {
            const xhr = new XMLHttpRequest();
            xhr.open("POST", `https://api.cloudinary.com/v1_1/${sig.cloud_name}/video/upload`);
            xhr.upload.onprogress = (ev) => ev.lengthComputable && setProgress(Math.round(ev.loaded / ev.total * 100));
            xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? res(JSON.parse(xhr.responseText)) : rej(new Error("upload failed"));
            xhr.onerror = () => rej(new Error("network error"));
            xhr.send(fd);
          });
          videoUrl = up.secure_url;
          toast.success("Video uploaded to Cloudinary");
        } catch (e) {
          toast.error("Cloudinary upload failed — paste a direct video URL instead");
          setSaving(false); setUploading(false);
          return;
        } finally { setUploading(false); }
      }

      const { data: content } = await api.post("/creator/content", {
        ...form, video_url: videoUrl,
        duration: Number(form.duration) || 0,
        pay_per_view_price: Number(form.pay_per_view_price) || 0,
        series_id: form.series_id || null,
        season: Number(form.season) || 0,
        episode_number: Number(form.episode_number) || 0,
      });
      await api.post(`/creator/content/${content.id}/submit`);
      toast.success("Uploaded & submitted for admin approval 🎬");
      setForm(emptyForm);
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setSaving(false); }
  };

  return (
    <DialogContent className="bg-[#110F18] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-display text-2xl">Upload New Content</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <Input label="Title" testid="up-title" value={form.title} onChange={(v) => upd("title", v)} />
        <div>
          <Lbl>Description</Lbl>
          <textarea data-testid="up-description" value={form.description} onChange={(e) => upd("description", e.target.value)} rows={3}
            className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 focus:border-[#E50914]/60 focus:outline-none resize-none" />
        </div>
        <Input label="Thumbnail URL" testid="up-thumbnail" value={form.thumbnail} onChange={(v) => upd("thumbnail", v)} placeholder="https://…" />

        {seriesList.length > 0 && (
          <div className="p-4 rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 space-y-3">
            <p className="text-sm font-semibold">Episode of a series (optional)</p>
            <Select label="Attach to series" testid="up-series" value={form.series_id} onChange={(v) => upd("series_id", v)} options={seriesList.map((s) => ({ value: s.id, label: s.title }))} />
            {form.series_id && (
              <div className="grid grid-cols-2 gap-4">
                <Input label="Season" testid="up-season" type="number" value={form.season} onChange={(v) => upd("season", v)} />
                <Input label="Episode #" testid="up-episode" type="number" value={form.episode_number} onChange={(v) => upd("episode_number", v)} />
              </div>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <Select label="Country" testid="up-country" value={form.country} onChange={(v) => upd("country", v)} options={COUNTRY_LIST.map((c) => ({ value: c.code, label: `${c.flag} ${c.name}` }))} />
          <Select label="Language" testid="up-language" value={form.language} onChange={(v) => upd("language", v)} options={LANGUAGES.map((l) => ({ value: l, label: l }))} />
          <Select label="Type" testid="up-type" value={form.content_type} onChange={(v) => upd("content_type", v)} options={CONTENT_TYPES.map((t) => ({ value: t, label: typeLabel(t) }))} />
          <Select label="Category" testid="up-category" value={form.category} onChange={(v) => upd("category", v)} options={categories.map((c) => ({ value: c.name, label: c.name }))} />
          <Input label="Duration (seconds)" testid="up-duration" type="number" value={form.duration} onChange={(v) => upd("duration", v)} />
          <Select label="Age Rating" testid="up-age" value={form.age_rating} onChange={(v) => upd("age_rating", v)} options={["G", "PG", "PG13", "16", "18"].map((r) => ({ value: r, label: r }))} />
        </div>

        <div className="p-4 rounded-xl border border-white/10 bg-white/5 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold"><Upload className="w-4 h-4 text-[#E50914]" /> Video Source</div>
          <p className="text-xs text-white/50">
            {config.cloudinary_enabled
              ? "Upload a video file — stored & delivered via Cloudinary CDN."
              : "Cloudinary not configured yet. Paste a direct video URL (mp4/HLS) to publish now."}
          </p>
          {config.cloudinary_enabled && (
            <input ref={fileRef} type="file" accept="video/*" data-testid="up-file"
              className="w-full text-sm file:mr-3 file:px-4 file:py-2 file:rounded-full file:border-0 file:bg-[#E50914] file:text-white" />
          )}
          <div className="relative">
            <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input data-testid="up-video-url" value={form.video_url} onChange={(e) => upd("video_url", e.target.value)} placeholder="https://…/video.mp4"
              className="w-full h-10 pl-10 pr-4 rounded-lg bg-white/5 border border-white/10 focus:border-[#E50914]/60 focus:outline-none text-sm" />
          </div>
          {uploading && <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-[#E50914]" style={{ width: `${progress}%` }} /></div>}
        </div>

        <div className="grid grid-cols-2 gap-4 items-center">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="up-premium" checked={form.subscription_required} onChange={(e) => upd("subscription_required", e.target.checked)} className="w-4 h-4 accent-[#E50914]" />
            <span className="text-sm">Premium (subscription required)</span>
          </label>
          <Input label="Pay-per-view price (R)" testid="up-ppv" type="number" value={form.pay_per_view_price} onChange={(v) => upd("pay_per_view_price", v)} />
        </div>

        <button onClick={createAndSubmit} disabled={saving} data-testid="up-submit"
          className="w-full h-11 rounded-lg bg-[#E50914] hover:bg-[#FF2A3B] font-semibold transition-colors disabled:opacity-60">
          {saving ? "Publishing…" : "Publish for Review"}
        </button>
      </div>
    </DialogContent>
  );
}

const Stat = ({ icon: Icon, label, value, gold }) => (
  <div className="glass rounded-2xl p-4">
    <Icon className={`w-5 h-5 mb-2 ${gold ? "text-[#D4AF37]" : "text-[#E50914]"}`} />
    <p className="font-display font-black text-2xl">{value}</p>
    <p className="text-xs text-white/50 mt-0.5">{label}</p>
  </div>
);
const Lbl = ({ children }) => <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">{children}</label>;
const Input = ({ label, testid, type = "text", value, onChange, placeholder }) => (
  <div><Lbl>{label}</Lbl><input data-testid={testid} type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
    className="w-full h-10 px-4 rounded-lg bg-white/5 border border-white/10 focus:border-[#E50914]/60 focus:outline-none text-sm" /></div>
);
const Select = ({ label, testid, value, onChange, options }) => (
  <div><Lbl>{label}</Lbl><select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
    className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 focus:border-[#E50914]/60 focus:outline-none text-sm">
    <option value="" className="bg-[#1A1724]">Select…</option>
    {options.map((o) => <option key={o.value} value={o.value} className="bg-[#1A1724]">{o.label}</option>)}
  </select></div>
);
